import {
  collection,
  documentId,
  doc,
  getDocs,
  limit,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';

export const RESERVATION_HOURS = 6;
export const MAX_ACTIVE_RESERVATIONS_PER_USER = 2;
const RESERVATIONS_COL = 'bookReservations';
const IN_CHUNK = 10;
const RESERVATION_BOOK_IDS_CACHE_TTL_MS = 45 * 1000;
const reservationByBookIdCache = new Map();

function nowMs() {
  return Date.now();
}

function getCacheKeyBookId(bookId) {
  return String(bookId || '');
}

function readReservationCache(bookId, now = nowMs()) {
  const key = getCacheKeyBookId(bookId);
  if (!key) return { hit: false, row: null };
  const entry = reservationByBookIdCache.get(key);
  if (!entry) return { hit: false, row: null };
  if (now - entry.ts > RESERVATION_BOOK_IDS_CACHE_TTL_MS) {
    reservationByBookIdCache.delete(key);
    return { hit: false, row: null };
  }
  return { hit: true, row: entry.row || null };
}

function writeReservationCache(bookId, row) {
  const key = getCacheKeyBookId(bookId);
  if (!key) return;
  reservationByBookIdCache.set(key, { ts: nowMs(), row: row || null });
}

function primeReservationCache(rows = []) {
  rows.forEach((row) => {
    const id = String(row?.bookId || row?.id || '');
    if (id) writeReservationCache(id, row);
  });
}

export function isReservationActive(row, atMs = nowMs()) {
  return (
    row &&
    row.status === 'active' &&
    Number.isFinite(Number(row.expiresAtMs)) &&
    Number(row.expiresAtMs) > atMs
  );
}

export async function listReservationsByIsbn(isbn) {
  if (!isbn) return [];
  const snap = await getDocs(
    query(
      collection(db, RESERVATIONS_COL),
      where('isbn', '==', isbn),
      limit(400),
    ),
  );
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  primeReservationCache(rows);
  return rows;
}

export async function listReservationsByCafeName(cafeName) {
  if (!cafeName) return [];
  const snap = await getDocs(
    query(
      collection(db, RESERVATIONS_COL),
      where('cafeName', '==', cafeName),
      limit(500),
    ),
  );
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  primeReservationCache(rows);
  return rows;
}

export async function listReservationsByUser(userUid, max = 30) {
  if (!userUid) return [];
  const snap = await getDocs(
    query(
      collection(db, RESERVATIONS_COL),
      where('userUid', '==', userUid),
      limit(max),
    ),
  );
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  primeReservationCache(rows);
  return rows;
}

export async function listActiveReservations(max = 1000) {
  const snap = await getDocs(
    query(
      collection(db, RESERVATIONS_COL),
      where('status', '==', 'active'),
      limit(max),
    ),
  );
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  primeReservationCache(rows);
  return rows;
}

export async function listReservationsByBookIds(bookIds = []) {
  const ids = Array.from(new Set((bookIds || []).filter(Boolean).map((x) => String(x))));
  if (ids.length === 0) return [];
  const now = nowMs();
  const out = [];
  const missing = [];

  ids.forEach((id) => {
    const cached = readReservationCache(id, now);
    if (cached.hit) {
      if (cached.row) out.push(cached.row);
      return;
    }
    missing.push(id);
  });

  for (let i = 0; i < missing.length; i += IN_CHUNK) {
    const pack = missing.slice(i, i + IN_CHUNK);
    if (pack.length === 0) continue;
    const snap = await getDocs(
      query(collection(db, RESERVATIONS_COL), where(documentId(), 'in', pack))
    );
    const byId = new Map();
    snap.docs.forEach((d) => {
      const row = { id: d.id, ...d.data() };
      byId.set(String(d.id), row);
      out.push(row);
    });
    pack.forEach((id) => {
      writeReservationCache(id, byId.get(String(id)) || null);
    });
  }
  return out;
}

export async function reserveBookInCafe({ user, isbn, cafeName, candidateBooks = [] }) {
  if (!user?.uid) {
    const e = new Error('Потрібна авторизація.');
    e.status = 401;
    throw e;
  }
  if (!isbn || !cafeName || candidateBooks.length === 0) {
    const e = new Error('Немає доступних примірників для бронювання.');
    e.status = 400;
    throw e;
  }

  const now = nowMs();
  const expiresAtMs = now + RESERVATION_HOURS * 60 * 60 * 1000;

  // Один активний reservation на цю книгу для користувача + глобальний ліміт активних броней.
  const ownRows = await listReservationsByUser(user.uid, 40);
  const ownActiveRows = ownRows.filter((x) => isReservationActive(x, now));
  const ownActive = ownActiveRows
    .find((x) => x.isbn === isbn && isReservationActive(x, now));
  if (ownActive) {
    return {
      ok: true,
      alreadyReserved: true,
      reservationId: ownActive.id,
      expiresAtMs: Number(ownActive.expiresAtMs),
      cafeName: ownActive.cafeName,
      bookId: ownActive.bookId,
    };
  }
  if (ownActiveRows.length >= MAX_ACTIVE_RESERVATIONS_PER_USER) {
    const e = new Error('Ви вже маєте 2 активні бронювання. Скасуйте одне, щоб забронювати іншу книгу.');
    e.status = 429;
    throw e;
  }

  for (const b of candidateBooks) {
    try {
      const result = await runTransaction(db, async (tx) => {
        const reservationRef = doc(db, RESERVATIONS_COL, String(b.id));
        const reservationSnap = await tx.get(reservationRef);
        const cur = reservationSnap.exists() ? reservationSnap.data() : null;

        if (isReservationActive(cur, now)) {
          if (cur.userUid === user.uid) {
            return {
              ok: true,
              alreadyReserved: true,
              reservationId: String(b.id),
              expiresAtMs: Number(cur.expiresAtMs),
              cafeName: cur.cafeName || cafeName,
              bookId: b.id,
            };
          }
          throw new Error('UNAVAILABLE_COPY');
        }

        tx.set(
          reservationRef,
          {
            bookId: String(b.id),
            isbn,
            cafeName: b.cafeName || cafeName,
            cafeAddress: b.cafeAddress || '',
            userUid: user.uid,
            userDisplayName: user.displayName || user.email || 'Користувач',
            status: 'active',
            expiresAtMs,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );

        return {
          ok: true,
          alreadyReserved: false,
          reservationId: String(b.id),
          expiresAtMs,
          cafeName: b.cafeName || cafeName,
          bookId: b.id,
        };
      });

      writeReservationCache(result.bookId, {
        id: String(result.bookId),
        bookId: String(result.bookId),
        isbn,
        cafeName: result.cafeName || cafeName,
        userUid: user.uid,
        userDisplayName: user.displayName || user.email || 'Користувач',
        status: 'active',
        expiresAtMs: result.expiresAtMs,
      });

      return result;
    } catch (err) {
      if (String(err?.message || '') === 'UNAVAILABLE_COPY') continue;
      throw err;
    }
  }

  const e = new Error('Немає вільних примірників у цій кавʼярні.');
  e.status = 409;
  throw e;
}

export async function cancelReservation({ userUid, bookId }) {
  if (!userUid || !bookId) return { ok: false };
  const reservationRef = doc(db, RESERVATIONS_COL, String(bookId));
  const txResult = await runTransaction(db, async (tx) => {
    const snap = await tx.get(reservationRef);
    if (!snap.exists()) return { ok: false };
    const row = snap.data();
    if (!isReservationActive(row)) return { ok: false };
    if (row.userUid !== userUid) {
      const e = new Error('Не можна скасувати чуже бронювання.');
      e.status = 403;
      throw e;
    }

    tx.set(
      reservationRef,
      {
        status: 'cancelled',
        cancelledAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
    return {
      ok: true,
      row,
    };
  });
  if (txResult?.ok) {
    writeReservationCache(bookId, {
      id: String(bookId),
      bookId: String(bookId),
      userUid: txResult.row?.userUid || userUid,
      status: 'cancelled',
      expiresAtMs: Number(txResult.row?.expiresAtMs) || 0,
    });
    return { ok: true };
  }
  return { ok: false };
}
